import type { Dispatch, SetStateAction } from "react";
import chatAgentService from "../../services/RealChatAgentService";
import type { Message, MessagePart, StreamChunk } from "../../types/chat.types";

/**
 * TRAC-497 (useChatStream slice 1): the uniform "card" chunk handlers extracted from
 * ChatInterface's streaming loop. Every entry here was a near-identical else-if branch:
 * append one MessagePart built from the chunk to the streaming message, then (usually)
 * persist the conversation. Declaring them as data instead of code means a new card
 * type is a 5-line table entry, not a 20-line branch in a 1,000-line function.
 *
 * Stateful chunk types (content, tool_*, ymmv_interface, error, done, agent_thinking,
 * trace_context, metrics) intentionally stay in ChatInterface — they touch loading /
 * scroll / YMMV / retry state that belongs to the shell.
 */

interface CardEntry {
  /** Build the MessagePart to append for this chunk. */
  build: (chunk: StreamChunk) => MessagePart;
  /**
   * Persist via saveMessagesToSession after appending. Every card persists; the
   * flag is kept so a future card with a genuine reason to stay in-memory can
   * declare it (and so the export's completeness contract stays explicit).
   *
   * TRAC-848: tire_browse_card used to set `persist: false`, carried over
   * verbatim when TRAC-497 extracted these branches out of ChatInterface. It was
   * never a product decision, and it meant the card silently disappeared from the
   * conversation on reload — invisible in the UI, and an unannounceable hole in a
   * downloaded transcript. Now persisted like every other card.
   */
  persist?: false;
}

const CARD_CHUNKS: Record<string, CardEntry> = {
  selection_cards: {
    build: (c) => ({
      type: "selection_cards",
      title: c.title,
      options: c.options,
      selectedValue: c.selectedValue,
      hint: c.hint,
      sourceTool: c.sourceTool,
    }),
  },
  source_cards: {
    build: (c) => ({
      type: "source_cards",
      title: c.title,
      options: c.options,
      sourceTool: c.sourceTool,
    }),
  },
  ranking_cards: {
    build: (c) => ({
      type: "ranking_cards",
      title: c.title,
      options: c.options,
      sourceTool: c.sourceTool,
    }),
  },
  rating_display: {
    build: (c) => ({
      type: "rating_display",
      title: c.title,
      overallRating: c.overallRating,
      reviewCount: c.reviewCount,
      metrics: c.metrics,
      sourceTool: c.sourceTool,
    }),
  },
  tdg_link_card: {
    build: (c) => ({
      type: "tdg_link_card",
      title: c.title,
      tdgUrl: c.tdgUrl,
      vehicle: c.vehicle,
      priorities: c.priorities,
      indicators: c.indicators,
      ctaText: c.ctaText,
      sourceTool: c.sourceTool,
    }),
  },
  // TRAC-295: unified tire-browse card (size search / category page / fallback).
  tire_browse_card: {
    build: (c) => ({
      type: "tire_browse_card",
      title: c.title,
      tireSize: c.tireSize,
      searchUrl: c.searchUrl,
      ctaText: c.ctaText,
      sourceTool: c.sourceTool,
    }),
  },
  size_search_link_card: {
    build: (c) => ({
      type: "size_search_link_card",
      tireSize: c.tireSize ?? c.tire_size,
      searchUrl: c.searchUrl ?? c.search_url,
      ctaText: c.ctaText ?? c.cta_text,
      // Staggered passthrough (TRAC-396 follow-up).
      isStaggered: c.isStaggered,
      frontTireSize: c.frontTireSize,
      rearTireSize: c.rearTireSize,
      sourceTool: c.sourceTool,
    }),
  },
  follow_up_actions: {
    build: (c) => ({
      type: "follow_up_actions",
      title: c.title,
      actions: c.actions,
      sourceTool: c.sourceTool,
    }),
  },
  // TRAC-732: NOTE — `human_handoff` is intentionally NOT a card here. It's a control
  // event handled statefully in ChatInterface's stream loop (sets the pending-offer flag);
  // the actual rendered card is a locally-appended `soft_handoff` part. See ChatInterface.
  // navigate_site: wayfinding page-link card (renders after the prose as its own block).
  navigation_card: {
    build: (c) => ({
      type: "navigation_card",
      url: c.url,
      title: c.title,
      whenToUse: c.whenToUse,
      navType: c.navType,
      tier: c.tier,
      sourceTool: c.sourceTool,
    }),
  },
  // TRAC-277: vehicle info card.
  vehicle_info_card: {
    build: (c) => ({
      type: "vehicle_info_card",
      title: c.title,
      vehicle: c.vehicle,
      sourceTool: c.sourceTool,
    }),
  },
  // TRAC-277: tire size confirmation.
  tire_size_confirmation: {
    build: (c) => ({
      type: "tire_size_confirmation",
      vehicleDescription: c.vehicleDescription,
      tireSize: c.tireSize,
      frontSize: c.frontSize,
      rearSize: c.rearSize,
      staggered: c.staggered,
      isFactory: c.isFactory,
      sourceTool: c.sourceTool,
    }),
  },
  tire_info_card: {
    build: (c) => ({
      type: "tire_info_card",
      title: c.title,
      tire: c.tire,
      sourceTool: c.sourceTool,
    }),
  },
  // New unified tire card with expand/collapse.
  tire_card: {
    build: (c) => ({
      type: "tire_card",
      title: c.title,
      tire: c.tire,
      sourceTool: c.sourceTool,
    }),
  },
};

/** The card chunk types this module handles (exported for tests / exhaustiveness checks). */
export const CARD_CHUNK_TYPES = Object.keys(CARD_CHUNKS);

/**
 * Append the card MessagePart for `chunk` to the streaming message `messageId`.
 * Returns true if the chunk type was a card handled here; false = not a card,
 * caller's dispatch continues (content / tools / lifecycle chunks).
 */
export function dispatchCardChunk(
  chunk: StreamChunk,
  messageId: string,
  setMessages: Dispatch<SetStateAction<Message[]>>,
): boolean {
  const entry = CARD_CHUNKS[chunk.type as string];
  if (!entry) return false;
  setMessages((prev) => {
    const updatedMessages = prev.map((msg) => {
      if (msg.id === messageId) {
        return { ...msg, parts: [...(msg.parts || []), entry.build(chunk)] };
      }
      return msg;
    });
    if (entry.persist !== false) {
      chatAgentService.saveMessagesToSession(updatedMessages);
    }
    return updatedMessages;
  });
  return true;
}
